import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/shadcn-ui/card';
import { Input } from '@/components/shadcn-ui/input';
import { Button } from '@/components/shadcn-ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn-ui/select';
import { Label } from '@/components/shadcn-ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn-ui/table';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';

const LIMIT = 10;
const AHJ_BASE_PATHS = ['/Ahj/v1.0', '/Ahj'];


const normalizePagination = (pagination = {}, fallbackTotal = 0) => {
  const total = Number(pagination.total || fallbackTotal || 0);
  const page = Number(pagination.page || 1);
  const limit = Number(pagination.limit || LIMIT);
  const totalPages = Number(pagination.totalPages || Math.max(1, Math.ceil(total / limit)));

  return { total, page, limit, totalPages };
};

const normalizeFilterValue = value => (value === 'all' ? '' : value);

const Ahjs = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ahjs, setAhjs] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [countyFilter, setCountyFilter] = useState('all');
  const [researchStatusFilter, setResearchStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityTierFilter, setPriorityTierFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: LIMIT, totalPages: 1 });
  const [filterOptions, setFilterOptions] = useState({
    county: [],
    research_status: [],
    type: [],
    priority_tier: [],
    region: [],
  });
  const [selectedAhj, setSelectedAhj] = useState(null);
  const [draft, setDraft] = useState({
    name: '',
    key: '',
    county: '',
    state: '',
    type: '',
    priority_tier: '',
    population: '',
  });

  const queryParams = useMemo(() => {
    const params = {
      search: debouncedSearch,
      sortBy: 'name',
      sortOrder: sortBy === 'name_desc' ? 'desc' : 'asc',
    };

    const county = normalizeFilterValue(countyFilter);
    const researchStatus = normalizeFilterValue(researchStatusFilter);
    const type = normalizeFilterValue(typeFilter);
    const priorityTier = normalizeFilterValue(priorityTierFilter);
    const region = normalizeFilterValue(regionFilter);

    if (county) params.county = county;
    if (researchStatus) params.research_status = researchStatus;
    if (type) params.type = type;
    if (priorityTier) params.priority_tier = priorityTier;
    if (region) params.region = region;

    return params;
  }, [
    debouncedSearch,
    countyFilter,
    researchStatusFilter,
    typeFilter,
    priorityTierFilter,
    regionFilter,
    sortBy,
  ]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);

    return () => clearTimeout(timeout);
  }, [search]);

  const requestAhjApi = useCallback(async ({ method = 'get', path = '', params, data }) => {
    let lastError;

    for (let index = 0; index < AHJ_BASE_PATHS.length; index += 1) {
      const basePath = AHJ_BASE_PATHS[index];
      try {
        const response = await appAxios({
          method,
          url: `${basePath}${path}`,
          params,
          data,
        });
        return response;
      } catch (error) {
        lastError = error;
        const status = error?.response?.status;
        const shouldTryNext = status === 404 || status === 405;
        if (!shouldTryNext || index === AHJ_BASE_PATHS.length - 1) {
          throw error;
        }
      }
    }

    throw lastError;
  }, []);

  const fetchAhjs = useCallback(async (page = 1, params = {}) => {
    setLoading(true);
    try {
      const response = await requestAhjApi({
        method: 'get',
        path: '',
        params: {
          page,
          limit: LIMIT,
          ...params,
        },
      });

      const payload = response?.data?.data;
      const rows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];

      setAhjs(rows);
      setPagination(normalizePagination(payload?.pagination, rows.length));
      setFilterOptions({
        county: payload?.filters?.county || [],
        research_status: payload?.filters?.research_status || [],
        type: payload?.filters?.type || [],
        priority_tier: payload?.filters?.priority_tier || [],
        region: payload?.filters?.region || [],
      });
    } catch (error) {
      console.error('Error fetching AHJs:', error);
      toast.error('Failed to fetch AHJs');
    } finally {
      setLoading(false);
    }
  }, [requestAhjApi]);

  useEffect(() => {
    fetchAhjs(1, queryParams);
  }, [queryParams, fetchAhjs]);

  const countyOptions = filterOptions.county;
  const researchStatusOptions = filterOptions.research_status;
  const typeOptions = filterOptions.type;
  const priorityTierOptions = filterOptions.priority_tier;
  const regionOptions = filterOptions.region;

  const handleSaveProfile = async () => {
    if (!selectedAhj?._id) return;

    if (!draft.name.trim() || !draft.key.trim()) {
      toast.error('Name and key are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        key: draft.key.trim(),
        county: draft.county.trim() || null,
        state: draft.state.trim() || null,
        type: draft.type.trim() || null,
        priority_tier: draft.priority_tier.trim() || null,
        population: draft.population?.trim() ? Number(draft.population.trim()) : null,
      };

      if (payload.population !== null && Number.isNaN(payload.population)) {
        toast.error('Population must be a valid number');
        return;
      }

      await requestAhjApi({
        method: 'patch',
        path: `/${selectedAhj._id}`,
        data: payload,
      });

      toast.success('AHJ profile updated successfully');
      setSelectedAhj(null);
      await fetchAhjs(pagination.page, queryParams);
    } catch (error) {
      console.error('Error updating AHJ profile:', error);
      toast.error(error?.response?.data?.message || 'Failed to update AHJ profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = nextPage => {
    if (nextPage < 1 || nextPage > pagination.totalPages || loading) return;
    fetchAhjs(nextPage, queryParams);
  };

  const visibleCount = pagination.total;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-3xl font-bold tracking-tight">AHJs</h2>
        <p className="text-muted-foreground">
          Master list of jurisdictions. Search and filters run on the server.
        </p>
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search by name, key, or state"
              className="pl-9"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
            <Select value={countyFilter} onValueChange={setCountyFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All counties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All counties</SelectItem>
                {countyOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={researchStatusFilter} onValueChange={setResearchStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All research statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All research statuses</SelectItem>
                {researchStatusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {typeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priorityTierFilter} onValueChange={setPriorityTierFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All priority tiers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priority tiers</SelectItem>
                {priorityTierOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All regions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                {regionOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger>
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Name (A → Z)</SelectItem>
                <SelectItem value="name_desc">Name (Z → A)</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center text-sm text-muted-foreground md:justify-end lg:justify-start">
              {visibleCount} of {pagination.total || visibleCount}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="overflow-hidden rounded-md border bg-card">
            <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>County</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>AHJ Type</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Population</TableHead>
                  </TableRow>
                </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                      Loading AHJs...
                    </TableCell>
                  </TableRow>
                ) : ahjs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                      No AHJs found for the selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  ahjs.map(ahj => (
                    <React.Fragment key={ahj._id}>
                      <TableRow>
                        <TableCell>
                          <div className="font-medium">{ahj.name || 'Unknown'}</div>
                          <div className="text-xs text-muted-foreground">{ahj.key || 'N/A'}</div>
                        </TableCell>
                        <TableCell>{ahj.state || 'Unknown'}</TableCell>
                        <TableCell>
                          <div className="font-medium">{ahj.county || 'Unknown'}</div>
                          <div className="text-xs text-muted-foreground">{ahj.region || 'N/A'}</div>
                        </TableCell>
                        <TableCell>{ahj.type || 'Unknown'}</TableCell>
                        <TableCell>{ahj.ahj_type || 'Unknown'}</TableCell>
                        <TableCell>{ahj.priority_tier || 'Unknown'}</TableCell>
                        <TableCell>
                          {Number(ahj.population || 0) > 0
                            ? Number(ahj.population).toLocaleString('en-US')
                            : 'Unknown'}
                        </TableCell>
                        
                      </TableRow>

                      {selectedAhj?._id === ahj._id && (
                        <TableRow className="bg-muted/40">
                          <TableCell colSpan={7} className="p-0">
                            <div className="grid gap-4 border-t p-4 lg:grid-cols-2">
                              <div className="grid gap-4 sm:grid-cols-2 lg:col-span-2">
                                <div className="space-y-2 sm:col-span-2">
                                  <Label>Name</Label>
                                  <Input
                                    value={draft.name}
                                    onChange={event => setDraft(prev => ({ ...prev, name: event.target.value }))}
                                  />
                                </div>

                                <div className="space-y-2 sm:col-span-2">
                                  <Label>Key</Label>
                                  <Input
                                    value={draft.key}
                                    onChange={event => setDraft(prev => ({ ...prev, key: event.target.value }))}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label>State</Label>
                                  <Input
                                    value={draft.state}
                                    onChange={event => setDraft(prev => ({ ...prev, state: event.target.value }))}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label>County</Label>
                                  <Input
                                    value={draft.county}
                                    onChange={event => setDraft(prev => ({ ...prev, county: event.target.value }))}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label>Type</Label>
                                  <Input
                                    value={draft.type}
                                    onChange={event => setDraft(prev => ({ ...prev, type: event.target.value }))}
                                  />
                                </div>

                                <div className="space-y-2">
                                  <Label>Priority Tier</Label>
                                  <Input
                                    value={draft.priority_tier}
                                    onChange={event => setDraft(prev => ({ ...prev, priority_tier: event.target.value }))}
                                  />
                                </div>

                                <div className="space-y-2 sm:col-span-2">
                                  <Label>Population</Label>
                                  <Input
                                    value={draft.population}
                                    onChange={event => setDraft(prev => ({ ...prev, population: event.target.value }))}
                                    type="number"
                                    min="0"
                                  />
                                </div>
                              </div>

                              <div className="flex items-center justify-end gap-2 lg:col-span-2">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedAhj(null);
                                  }}
                                  disabled={saving}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={() => {
                                    handleSaveProfile();
                                  }}
                                  disabled={saving}
                                >
                                  {saving ? 'Saving...' : 'Save Changes'}
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loading}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages || loading}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Ahjs;
